/**
 * BookPage — reader view for a user-curated book page.
 *
 * Fetches by ID (no publish step required; books are immediately viewable).
 * URL: /wire/books/:id
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Link } from 'lucide-react'
import { usePage } from '../api/hooks/usePages'
import { PageSlotRenderer, usePublishedSlotArticles } from '../components/page-renderer/PageSlotRenderer'
import PageLoader from '../components/common/PageLoader'
import type { CanvasSlot } from '../components/editor-shared/canvasTypes'
import type { PageBlock, BlockContent } from '../api/types'

// ─── Block conversion helpers (shared pattern across reader pages) ─────────────

function pageBlocksToSlots(blocks: PageBlock[]): CanvasSlot[] {
  return blocks
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((b) => ({
      id: b.id,
      type: b.type,
      order: b.order,
      config: b.config,
      columnBlocks: b.columnContent
        ? Object.fromEntries(
            Object.entries(b.columnContent).map(([k, colBlocks]) => [
              k,
              (colBlocks ?? []).map((cb) => ({ id: cb.id, type: cb.type })),
            ]),
          )
        : undefined,
    }))
}

function pageBlocksToContentMap(blocks: PageBlock[]): Record<string, BlockContent> {
  const map: Record<string, BlockContent> = {}
  for (const b of blocks) {
    if (b.content) map[b.id] = b.content
    if (b.columnContent) {
      for (const [colKey, colBlocks] of Object.entries(b.columnContent)) {
        for (const cb of colBlocks ?? []) {
          if (cb.content) map[`${b.id}:${colKey}:${cb.id}`] = cb.content
        }
      }
    }
  }
  return map
}

// ─── Book renderer ─────────────────────────────────────────────────────────────

function BookView({ title, blocks, navigate, pageId }: {
  title: string
  blocks: PageBlock[]
  navigate: ReturnType<typeof useNavigate>
  pageId: string
}) {
  const slots = pageBlocksToSlots(blocks)
  const contentMap = pageBlocksToContentMap(blocks)
  const { slotArticleMap, isPending } = usePublishedSlotArticles(slots, contentMap)
  const [copied, setCopied] = useState(false)

  function handleShare() {
    const url = `${window.location.origin}/wire/books/${pageId}?nav_method=gen_link`
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  if (isPending) return <PageLoader />

  return (
    <div className="mx-auto max-w-7xl px-6 pb-12 pt-6">
      <div className="mb-8 border-b border-line pb-5">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-fg-secondary">
              Book
            </div>
            <h1 className="mt-1 font-zodiak text-4xl font-bold text-fg">{title}</h1>
          </div>
          <button
            type="button"
            onClick={handleShare}
            className="flex shrink-0 items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
          >
            <Link size={13} aria-hidden="true" />
            {copied ? 'Copied!' : 'Share'}
          </button>
        </div>
      </div>
      <div className="space-y-10">
        {Object.values(slotArticleMap).every((a) => a.length === 0) ? (
          <div className="flex h-48 items-center justify-center text-sm text-fg-secondary">
            No content available.
          </div>
        ) : (
          slots.map((slot) => (
            <PageSlotRenderer
              key={slot.id}
              slot={slot}
              articles={slotArticleMap[slot.id] ?? []}
              slotArticleMap={slotArticleMap}
              onArticleClick={(article) =>
                navigate(`/wire/articles/${article.id}`, { state: { navMethod: 'page', pageId } })
              }
            />
          ))
        )}
      </div>
    </div>
  )
}

// ─── Page component ────────────────────────────────────────────────────────────

export default function BookPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: page, isPending, isError } = usePage(id)

  if (isPending) return <PageLoader />

  if (isError || !page) {
    return (
      <div className="mx-auto max-w-7xl px-6 pb-12 pt-6">
        <div className="mb-8 border-b border-line pb-5">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-fg-secondary">
            Book
          </div>
          <h1 className="mt-1 font-zodiak text-4xl font-bold text-fg">Book Not Found</h1>
        </div>
        <div className="flex h-48 items-center justify-center text-sm text-fg-secondary">
          This book is no longer available.
        </div>
      </div>
    )
  }

  return (
    <BookView
      title={page.title}
      blocks={page.blocks}
      pageId={page.id}
      navigate={navigate}
    />
  )
}
